import React, { useState } from 'react';
import { Modal, Form, Select, DatePicker, Input, message } from 'antd';
import {
  FileAddOutlined,
} from '@ant-design/icons';
import { generateRegulatoryReport } from '../../services/regulatoryReportingService';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const REPORT_TYPES = [
  { value: 'CBN_RETURNS', label: 'CBN Returns' },
  { value: 'NDIC_RETURNS', label: 'NDIC Returns' },
  { value: 'AML_CTR', label: 'AML — Currency Transaction Report (CTR)' },
  { value: 'AML_SAR', label: 'AML — Suspicious Activity Report (SAR)' },
  { value: 'AML_STR', label: 'AML — Suspicious Transaction Report (STR)' },
  { value: 'NFIU_REPORT', label: 'NFIU Report' },
  { value: 'EFCC_REPORT', label: 'EFCC Compliance Report' },
  { value: 'CREDIT_BUREAU', label: 'Credit Bureau Report' },
  { value: 'CAPITAL_ADEQUACY', label: 'Capital Adequacy Report' },
  { value: 'LIQUIDITY_REPORT', label: 'Liquidity Report' },
  { value: 'PRUDENTIAL_RETURNS', label: 'Prudential Returns' },
  { value: 'RISK_ASSESSMENT', label: 'Risk Assessment Report' },
  { value: 'KYC_COMPLIANCE', label: 'KYC Compliance Report' },
  { value: 'TRANSACTION_MONITORING', label: 'Transaction Monitoring Report' },
  { value: 'CUSTOM', label: 'Custom Report' },
];

const GenerateReportModal = ({ visible, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        reportType: values.reportType,
        startDate: values.reportingPeriod[0].format('YYYY-MM-DD'),
        endDate: values.reportingPeriod[1].format('YYYY-MM-DD'),
        description: values.description || '',
      };

      const res = await generateRegulatoryReport(payload);
      if (res?.status) {
        message.success('Report generated successfully');
        form.resetFields();
        onSuccess?.();
        onClose();
      } else {
        message.error(res?.message || 'Failed to generate report');
      }
    } catch (err) {
      if (err?.errorFields) return; // validation error
      console.error('Failed to generate report:', err);
      message.error('An error occurred while generating the report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
          <FileAddOutlined style={{ color: '#ff6700' }} />
          Generate Regulatory Report
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Generate Report"
      okButtonProps={{
        loading,
        style: {
          background: '#ff6700',
          borderColor: '#ff6700',
          borderRadius: 8,
          fontWeight: 700,
        },
      }}
      cancelButtonProps={{
        style: { borderRadius: 8 },
      }}
      width={600}
      className="reg-form-modal"
      centered
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          name="reportType"
          label="Report Type"
          rules={[{ required: true, message: 'Please select a report type' }]}
        >
          <Select
            placeholder="Select report type"
            size="large"
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
          >
            {REPORT_TYPES.map((rt) => (
              <Option key={rt.value} value={rt.value}>
                {rt.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="reportingPeriod"
          label="Reporting Period"
          rules={[{ required: true, message: 'Please select the reporting period' }]}
        >
          <RangePicker
            size="large"
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description (Optional)"
        >
          <TextArea
            rows={3}
            placeholder="Add any notes or context for this report..."
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default GenerateReportModal;
